import { IcoFilter, IcoSquareFilter } from '@icons';
import React, { useState } from 'react';
import { Button, Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
// import { useLocation } from 'react-router-dom';
import SearchCheckboxAdvancedFilter from './SearchCheckboxAdvancedFilter';
import { wrapQueryParams } from '@components/project/Sampling/CustomFilter/queryParamsUtils';
import AdvancedFilter from './AdvancedFilter';
import ResetFilter from './ResetFilter';
import BooleanOption from './BooleanOption';
// import useFilterRedirect from './useFilterRedirect';

export type FilterFieldType = {
	name: string;
	label: string;
	queryParamName?: string;
	options?: { value: string; label: string }[];
	searchCheckboxPlaceholder?: string;
	searchCheckboxAdvancedFilter?: boolean;
	optionsType?: 'static' | 'dynamic';
	advancedOptions?: {
		name: string;
		label: string;
		switchLabelPrefix?: string;
		switchLabelSuffix?: string;
		options?: { value: string; label: string }[];
		type: 'static' | 'boolean' | 'boolean-extra' | 'control';
		needsEntityType?: boolean;
		extraQueryParams?: { name: string; value: string }[];
	}[];
	booleanLabel?: string;
	pluralLabel?: string;
	type?: 'boolean';
	entityType?: string;
	needsEntityType?: boolean;
};

interface CustomFilterProps {
	filterFields: FilterFieldType[];
	activeFilters: { [key: string]: string[] };
	setActiveFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
	queryParams: string | undefined;
	setQueryParams: React.Dispatch<React.SetStateAction<string | undefined>>;
	pageName: string;
	projectName: string;
	hasNewButton?: boolean;
}

export const MIN_OPTIONS_COUNT_FOR_ADVANCED_FILTER = 5;

const CustomFilter = ({
	filterFields,
	setQueryParams,
	activeFilters,
	setActiveFilters,
	pageName,
	projectName,
	hasNewButton = false,
}: CustomFilterProps) => {
	const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);

	const [initialFilters, setInitialFilters] = useState<{ [key: string]: string[] }>({});

	// const location = useLocation();
	// const isCompleteList = location.search.includes('complete-list=true');

	const setNewQueryParams = (newParams: { name: string; value: string[] }[]) => {
		const queryParamsToSet: Record<string, string[]> = {};

		newParams.forEach(({ name, value }) => {
			if (value.length > 0) {
				queryParamsToSet[name] = value;
			}
		});

		setQueryParams(wrapQueryParams(queryParamsToSet));
	};

	const handleFilterSet = (field: FilterFieldType, option: { value: string }, isActive: boolean) => {
		setActiveFilters((prevActiveFilters) => {
			const newFilters = { ...prevActiveFilters };

			if (isActive) {
				if (newFilters[field.queryParamName]) {
					newFilters[field.queryParamName] = newFilters[field.queryParamName].filter((item) => item !== option.value);
					if (newFilters[field.queryParamName].length === 0) {
						delete newFilters[field.queryParamName];
					}
				}
			} else {
				newFilters[field.queryParamName] = [...(newFilters[field.queryParamName] || []), option.value];
				if (field.entityType && field.needsEntityType) {
					newFilters.entity_type = [field.entityType];
				}
			}

			const hasEntityTypeDependency = Object.keys(newFilters).some((activeFilterKey) => {
				const activeFilterField = filterFields.find(
					(filterField) => filterField.queryParamName === activeFilterKey || `${filterField.entityType}_question_code` === activeFilterKey,
				);
				return activeFilterField?.needsEntityType;
			});

			const finalFilters = hasEntityTypeDependency
				? newFilters
				: (() => {
						const { entity_type, ...filtersWithoutEntity } = newFilters;
						return filtersWithoutEntity;
					})();
			return finalFilters;
		});
	};

	const handleFilterReset = () => {
		setInitialFilters(activeFilters);
		setActiveFilters((prevActiveFilters) => {
			const newFilters = Object.entries(prevActiveFilters).filter(([key]) => key === 'sampling');
			return Object.fromEntries(newFilters);
		});
	};

	// const handleFilterRedirect = useFilterRedirect();

	return (
		<>
			{hasNewButton ? (
				<Button
					onClick={() => {
						setIsOffcanvasOpen(true);
					}}
					className="btn custom-ghost-button"
					style={{
						fontWeight: 500,
						fontSize: '18px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '0.5rem',
						textTransform: 'none',
						padding: 0,
					}}
				>
					<IcoSquareFilter fill="#1C2743" />
					filtri
				</Button>
			) : (
				<OverlayTrigger placement="top" overlay={<Tooltip>Filtri</Tooltip>}>
					<Button
						onClick={() => {
							setIsOffcanvasOpen(true);
						}}
						className="btn custom-ghost-button"
					>
						<IcoFilter />
					</Button>
				</OverlayTrigger>
			)}
			<Offcanvas
				show={isOffcanvasOpen}
				onHide={() => {
					setIsOffcanvasOpen(false);
				}}
				style={{
					backgroundColor: '#EEF1F2',
					width: '500px',
				}}
			>
				<Offcanvas.Header
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Offcanvas.Title
						className="fw-bold"
						style={{
							fontSize: '1.5rem',
						}}
					>
						Filtri
					</Offcanvas.Title>
					{filterFields.some((field) => field.advancedOptions && field.advancedOptions.length > 0) ? (
						<AdvancedFilter
							pageName={pageName}
							projectName={projectName}
							filterFields={filterFields}
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							handleFilterSet={handleFilterSet}
							handleFilterReset={handleFilterReset}
							initialFilters={initialFilters}
							setInitialFilters={setInitialFilters}
						/>
					) : null}
				</Offcanvas.Header>
				<Offcanvas.Body
					style={{
						padding: '1rem',
						display: 'grid',
						gridTemplateRows: '1fr auto',
						gap: '2.5rem',
					}}
				>
					<div
						style={{
							backgroundColor: '#fff',
							borderRadius: '0.5rem',
							padding: '1rem',
							height: '100%',
							display: 'flex',
							gap: '1.5rem',
							flexDirection: 'column',
							overflowY: 'auto',
						}}
					>
						{filterFields.map((field) => (
							<div
								key={field.name}
								style={{
									marginBlock: '1rem',
									display: 'grid',
									gap: '1rem',
								}}
							>
								<h5 className="fw-bold">{field.label}</h5>
								<div
									style={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: '0.5rem',
										overflow: 'hidden',
									}}
								>
									{!('type' in field) && field.type !== 'boolean'
										? field.options.map((item, index) => {
												if (index >= MIN_OPTIONS_COUNT_FOR_ADVANCED_FILTER && field.searchCheckboxAdvancedFilter) return null; // mostro solo i primi 5, gli altri vanno nello searchcheckboxadvanced
												const fieldIndex = field.queryParamName || field.name;
												const isActive = activeFilters[fieldIndex]?.includes(item.value);
												return (
													<Button
														key={item.value}
														onClick={() => {
															handleFilterSet(field, item, isActive);
														}}
														className={`btn ${isActive ? 'custom-primary-button' : 'custom-outline-button'}`}
														style={{
															borderRadius: '20px',
															textTransform: 'none',
															padding: '0.25rem 1rem',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															maxWidth: '432px',
															fontWeight: 500,
														}}
													>
														{item.label}
													</Button>
												);
											})
										: null}
									{field.searchCheckboxAdvancedFilter && field.options.length > MIN_OPTIONS_COUNT_FOR_ADVANCED_FILTER ? (
										<SearchCheckboxAdvancedFilter field={field} activeFilters={activeFilters} handleFilterSet={handleFilterSet} />
									) : null}
									{'type' in field && field.type === 'boolean' ? (
										<BooleanOption field={field} activeFilters={activeFilters} setActiveFilters={setActiveFilters} />
									) : // <div
									// 	style={{
									// 		borderRadius: '8px',
									// 		padding: '0.5rem',
									// 		border: '1px solid #C7CFD7',
									// 		display: 'flex',
									// 		alignItems: 'center',
									// 		justifyContent: 'space-between',
									// 		width: '100%',
									// 	}}
									// >
									// 	{field.booleanLabel}
									// 	<Form.Check
									// 		type="switch"
									// 		className="custom-switch"
									// 		id={`custom-switch-${field.name}`}
									// 		checked={activeFilters[field.name]?.[0] === field.options?.[0]?.value}
									// 		onChange={(event) => {
									// 			const isChecked = event.target.checked;
									// 			setActiveFilters((prev) => ({
									// 				...prev,
									// 				[field.name]: [field.options?.[isChecked ? 0 : 1]?.value],
									// 			}));
									// 		}}
									// 	/>
									// </div>
									null}
								</div>
							</div>
						))}
					</div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<ResetFilter handleFilterReset={handleFilterReset} activeFilters={activeFilters} />
						<div
							style={{
								display: 'flex',
								gap: '0.5rem',
							}}
						>
							<Button
								onClick={() => {
									setIsOffcanvasOpen(false);
									if (Object.values(initialFilters).length === 0) return;
									setTimeout(handleFilterReset, 300);
								}}
								className="btn custom-ghost-button"
								style={{
									textTransform: 'none',
								}}
							>
								Annulla
							</Button>
							<Button
								onClick={() => {
									setNewQueryParams(Object.entries(activeFilters).map(([name, value]) => ({ name, value })));
									setIsOffcanvasOpen(false);
									// handleFilterRedirect(activeFilters, isCompleteList);
								}}
								className="btn custom-primary-button"
								style={{
									textTransform: 'none',
								}}
							>
								Conferma
							</Button>
						</div>
					</div>
				</Offcanvas.Body>
			</Offcanvas>
		</>
	);
};

export default CustomFilter;
